// EditContact.jsx
// Route: /editcontact/:contactId
// Add to router: <Route path="/editcontact/:contactId" element={<EditContact />} />

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const ADDRESS_TYPES = ["Billing", "Shipping", "Home", "Work", "Other"];
const emptyAddress = () => ({ addressType: "Home", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
      <div role="dialog" aria-modal="true" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1055, width: "100%", maxWidth: 420, background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px" }}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff0f0" }}>
            <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
          </div>
          <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>{title}</h6>
        </div>
        <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>{message}</p>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-sm btn-danger px-4" style={{ borderRadius: 8 }} onClick={onConfirm}>Delete</button>
          <button className="btn btn-sm btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </>
  );
};

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center" style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }} onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function EditContact() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", designation: "", department: "", addresses: [], notes: "", primary: false,
  });
  const [customers, setCustomers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("address");
  const [activeSubTab, setActiveSubTab] = useState("systemNotes");
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [saving, setSaving] = useState(false);

  const [addressModal, setAddressModal] = useState(false);
  const [editAddressIdx, setEditAddressIdx] = useState(null);
  const [addressDraft, setAddressDraft] = useState(emptyAddress());
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });
  const closeConfirm = () => setConfirmModal({ show: false, title: "", message: "", onConfirm: null });

  // Bootstrap loader
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const il = document.createElement("link"); il.rel = "stylesheet"; il.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(il);
    return () => { bl.remove(); il.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) { toast.error("Unauthorized."); navigate("/"); return; }
  }, [navigate, token]);

  // Navbar color
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  // Load all customers for re-assignment dropdown
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setCustomers(Array.isArray(data) ? data : data.content || []))
      .catch(() => { });
  }, [token]);

  // Load contact
  useEffect(() => {
    if (!contactId) return;
    fetch(`${API_BASE_URL}/api/contacts/${contactId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setFormData(data); else toast.error("Contact not found."); })
      .catch(() => toast.error("Failed to load contact."));
  }, [contactId, token]);

  // Load audit logs
  useEffect(() => {
    if (!contactId) return;
    fetch(`${API_BASE_URL}/api/audit/contact/${contactId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(setAuditLogs).catch(() => setAuditLogs([]));
  }, [contactId, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleAddressChange = (idx, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.addresses || [])];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, addresses: updated };
    });
  };
  const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...(prev.addresses || []), emptyAddress()] }));
  const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: (prev.addresses || []).filter((_, i) => i !== idx) }));

  const handleSetPrimary = async () => {
    if (!formData.customerId) return;
    try {
      await fetch(`${API_BASE_URL}/api/customers/${formData.customerId}/contacts/${contactId}/primary`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      toast.success("Set as primary contact.");
      setFormData((prev) => ({ ...prev, primary: true }));
    } catch { toast.error("Failed to set primary."); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) { toast.warning("Contact name is required."); return; }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.warning("Valid email is required."); return; }
    setSaving(true);
    try {
      // Use global contact endpoint (PUT /api/contacts/{id})
      const res = await fetch(`${API_BASE_URL}/api/contacts/${contactId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Contact updated successfully!");
      navigate(`/viewcontact/${contactId}`);
    } catch (err) {
      toast.error(err.message || "Failed to update contact.");
    } finally {
      setSaving(false);
    }
  };

  const openAddressModal = (idx = null) => {
    setEditAddressIdx(idx);
    setAddressDraft(idx !== null ? { ...(formData.addresses || [])[idx] } : emptyAddress());
    setAddressModal(true);
  };

  const saveAddressModal = () => {
    setFormData((prev) => {
      const updated = [...(prev.addresses || [])];
      if (editAddressIdx !== null) {
        updated[editAddressIdx] = addressDraft;
      } else {
        updated.push(addressDraft);
      }
      return { ...prev, addresses: updated };
    });
    setAddressModal(false);
  };

  return (
    <>
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
      <div className="fixed-top"><AdminNavbar /></div>

      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* Breadcrumb */}
        <nav className="mb-2" style={{ fontSize: 13, color: "#888" }}>
          <Link to="/allcontacts" style={{ color: "#0052CC", textDecoration: "none" }}>Contacts</Link>
          <span className="mx-1">/</span>
          {formData.name
            ? <Link to={`/viewcontact/${contactId}`} style={{ color: "#0052CC", textDecoration: "none" }}>{formData.name}</Link>
            : <span>…</span>}
          <span className="mx-1">/</span>
          <span className="text-dark fw-semibold">Edit</span>
        </nav>

        <div className="mb-3">
          <h4 className="m-0">
            <i className="bi bi-person-gear me-2"></i>
            {formData.name ? formData.name : "Contact"}
          </h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="submit" form="editContactForm" className="btn btn-primary me-2" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
            </div>
            <Link to="/allcontacts" className="text-primary text-decoration-none me-3">List</Link>
          </div>
        </div>

        <form id="editContactForm" onSubmit={handleSubmit}>

          {/* Customer assignment (read-only display + optional reassign) */}
          <Section title="Customer">
            <div className="col-md-4">
              <label className="form-label">Assigned Customer</label>
              <select name="customerId" className="form-select" value={formData.customerId || ""} onChange={handleChange}>
                <option value="">— Select customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.customerName} ({c.customerCode})</option>
                ))}
              </select>
              <div className="form-text">You can reassign this contact to a different customer.</div>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div>
                <div className="d-flex align-items-center gap-3">
                  <div className="form-check mb-0">
                    <input type="checkbox" className="form-check-input" id="primary" name="primary" checked={!!formData.primary} onChange={handleChange} />
                    <label className="form-check-label fw-semibold" htmlFor="primary">Primary Contact</label>
                  </div>
                  {!formData.primary && formData.customerId && (
                    <button type="button" className="btn btn-sm btn-outline-success" onClick={handleSetPrimary}>
                      <i className="bi bi-star me-1" />Set as Primary
                    </button>
                  )}
                  {formData.primary && (
                    <span className="badge" style={{ background: "#1d4ed8", color: "#fff" }}>★ Currently Primary</span>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* Contact details */}
          <Section title="Contact Information">
            <div className="col-md-4">
              <label className="form-label">Full Name <span className="text-danger">*</span></label>
              <input type="text" name="name" className="form-control" value={formData.name || ""} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Email Address <span className="text-danger">*</span></label>
              <input type="email" name="email" className="form-control" value={formData.email || ""} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Phone Number</label>
              <input type="tel" name="phone" className="form-control" value={formData.phone || ""} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Designation / Role</label>
              <input type="text" name="designation" className="form-control" value={formData.designation || ""} onChange={handleChange} placeholder="e.g. Finance Manager" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Department</label>
              <input type="text" name="department" className="form-control" value={formData.department || ""} onChange={handleChange} placeholder="e.g. Finance, HR" />
            </div>
          </Section>

        </form>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
          {[["address", "Address"], ["details", "Notes"], ["system", "System Information"]].map(([tabId, label]) => (
            <li className="nav-item" key={tabId}>
              <button type="button" className={`nav-link ${activeTab === tabId ? "active" : ""}`} onClick={() => setActiveTab(tabId)}
                style={{ color: activeTab === tabId ? navbarColor : "#fff", backgroundColor: activeTab === tabId ? "#fff" : "transparent", fontWeight: activeTab === tabId ? "600" : "normal" }}>
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="border p-3 mb-3">
          {activeTab === "address" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold m-0">
                  <i className="bi bi-geo-alt me-2"></i>Addresses ({(formData.addresses || []).length})
                </h6>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => openAddressModal()}>
                  <i className="bi bi-plus me-1"></i> Add Address
                </button>
              </div>

              {(formData.addresses || []).length === 0 ? (
                <p className="text-muted">No addresses added yet.</p>
              ) : (
                <table className="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Type</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Address</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>City</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>State</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Country</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Postal Code</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData.addresses || []).map((addr, idx) => (
                      <tr key={idx}>
                        <td><span className="badge bg-secondary">{addr.addressType || "Other"}</span></td>
                        <td>{[addr.addressLine1, addr.addressLine2].filter(Boolean).join(", ") || "—"}</td>
                        <td>{addr.city || "—"}</td>
                        <td>{addr.state || "—"}</td>
                        <td>{addr.country || "—"}</td>
                        <td>{addr.postalCode || "—"}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openAddressModal(idx)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setConfirmModal({
                              show: true,
                              title: "Remove Address",
                              message: `Are you sure you want to remove this ${addr.addressType || "address"} address ? This action cannot be undone.`,
                              onConfirm: () => { closeConfirm(); removeAddress(idx); },
                            })}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === "details" && (
            <Section title="Internal Notes">
              <div className="col-md-12">
                <label className="form-label">Notes</label>
                <textarea name="notes" className="form-control" rows={4} value={formData.notes || ""} onChange={handleChange} placeholder="Any additional notes about this contact…" />
              </div>
            </Section>
          )}

          {activeTab === "system" && (
            <div className="p-3 border rounded bg-white">
              <div className="mb-3">
                {/* <div className="form-check mb-2">
                  <input type="checkbox" className="form-check-input" checked={!!formData.primary} disabled readOnly />
                  <label className="form-check-label fw-bold ms-2">PRIMARY CONTACT</label>
                </div> */}
                {formData.createdAt && <div className="text-muted small"><strong>Date Created:</strong> {formatDateTime(formData.createdAt)}</div>}
              </div>
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>System Notes</button>
                </li>
              </ul>
              {activeSubTab === "systemNotes" && (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                      <th style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.length === 0
                      ? <tr><td colSpan="5" className="text-center text-muted">No records to show.</td></tr>
                      : auditLogs.map((log, i) => (
                        <tr key={i}>
                          <td>{formatDateTime(log.modifiedDate)}</td>
                          <td>{log.modifiedBy}</td>
                          <td>{log.fieldName}</td>
                          <td>{log.oldValue || "—"}</td>
                          <td>{log.newValue || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center mt-2 mb-4">
          <button type="submit" form="editContactForm" className="btn btn-primary me-2" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </div>


      {/* Address Modal */}
      {addressModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: "#DFE4EB" }}>
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-geo-alt me-2"></i>
                  {editAddressIdx !== null ? "Edit Address" : "Add Address"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setAddressModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Address Type</label>
                    <select className="form-select" value={addressDraft.addressType} onChange={(e) => setAddressDraft(p => ({ ...p, addressType: e.target.value }))}>
                      {ADDRESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">Address Line 1</label>
                    <input type="text" className="form-control" value={addressDraft.addressLine1} onChange={(e) => setAddressDraft(p => ({ ...p, addressLine1: e.target.value }))} placeholder="Street address" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Address Line 2</label>
                    <input type="text" className="form-control" value={addressDraft.addressLine2} onChange={(e) => setAddressDraft(p => ({ ...p, addressLine2: e.target.value }))} placeholder="Apartment, suite, building (optional)" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">City</label>
                    <input type="text" className="form-control" value={addressDraft.city} onChange={(e) => setAddressDraft(p => ({ ...p, city: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">State / Province</label>
                    <input type="text" className="form-control" value={addressDraft.state} onChange={(e) => setAddressDraft(p => ({ ...p, state: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Country</label>
                    <input type="text" className="form-control" value={addressDraft.country} onChange={(e) => setAddressDraft(p => ({ ...p, country: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Postal Code</label>
                    <input type="text" className="form-control" value={addressDraft.postalCode} onChange={(e) => setAddressDraft(p => ({ ...p, postalCode: e.target.value }))} placeholder="PIN / ZIP" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={saveAddressModal}>
                  {editAddressIdx !== null ? "Update Address" : "Add Address"}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAddressModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}